import { Download, Plus } from "lucide-react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { redirectToLogin } from "../../utils/auth";

// RCS external-client ticket list. External-only: no assignee/deadline/org
// columns and no internal swimlane/history access.
export const Tickets = ({
  tickets,
  setCount,
  requestOptions,
  priorities,
  status,
  activeIndex,
  username,
}) => {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedButton, setSelectedButton] = useState("allTickets");
  const [filteredTickets, setFilteredTickets] = useState([]);
  const [filteredByStates, setFilteredByStates] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt"); // default sort
  const [sortDir, setSortDir] = useState("desc"); // newest first by default
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(5);

  const authToken = localStorage.getItem("authToken");

  const handleButtonClick = (button) => {
    setSelectedButton(button);
  };

  function downLoadTickets() {
    // Export the currently filtered tickets (all active filters applied,
    // pagination ignored) straight from the on-screen data — no API call.
    // Full ticket detail, written as a real .xlsx via SheetJS.
    const rows = filteredByStates;
    if (!rows.length) {
      alert("No tickets to download for the current filters.");
      return;
    }

    const safe = (fn, v) => {
      try { return fn(v); } catch { return ""; }
    };
    const dOnly = (v) => (v ? new Date(v).toLocaleDateString() : "");
    const dTime = (v) => (v ? new Date(v).toLocaleString() : "");
    const deadlineStatus = (v) =>
      v ? (new Date(v) < new Date() ? "Crossed" : "On Time") : "";

    const columns = [
      { header: "Ticket ID", value: (t) => t.ticketId },
      { header: "Title", value: (t) => t.title || "" },
      { header: "Description", value: (t) => t.description || "" },
      { header: "Service Request Type", value: (t) => safe(getServiceRequest, t.requestType) },
      { header: "Issue At", value: (t) => t.issueAt || "" },
      { header: "Severity", value: (t) => safe(getPriority, t.priority) },
      { header: "Status", value: (t) => safe(getStatus, t.status) },
      { header: "Deadline", value: (t) => dTime(t.deadline) },
      { header: "Deadline Status", value: (t) => deadlineStatus(t.deadline) },
      { header: "Collaborators", value: (t) => t.externalCollaborators || "" },
      { header: "Created By", value: (t) => t.createdBy || "" },
      { header: "Resolution", value: (t) => t.resolution || "" },
      { header: "Created Date", value: (t) => dOnly(t.createdAt) },
      { header: "Last Updated", value: (t) => dOnly(t.updatedAt) },
    ];

    const header = columns.map((c) => c.header);
    const data = rows.map((t) =>
      Object.fromEntries(columns.map((c) => [c.header, c.value(t)])),
    );
    const ws = XLSX.utils.json_to_sheet(data, { header });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tickets");
    XLSX.writeFile(wb, "tickets.xlsx");
  }

  useEffect(() => {
    let tempTickets = tickets;

    if (selectedButton === "myRaised") {
      tempTickets = tempTickets.filter((ticket) => ticket.createdBy == username);
    } else if (selectedButton === "Collaborators") {
      tempTickets = tempTickets.filter((ticket) => {
        const collaboratorsArray = (ticket.collaborators || "")
          .split(",")
          .map((email) => email.trim());
        return collaboratorsArray.includes(username);
      });
    }

    if (searchQuery.trim() !== "") {
      const lowerCaseQuery = searchQuery.toLowerCase();

      const matches = (ticket, query) =>
        ticket.ticketId.toLowerCase().includes(query) ||
        getServiceRequest(ticket.requestType).toLowerCase().includes(query) ||
        getPriority(ticket.priority).toLowerCase().includes(query) ||
        getStatus(ticket.status).toLowerCase().includes(query) ||
        ticket.updatedAt.toLowerCase().includes(query);

      if (lowerCaseQuery.includes(" and ")) {
        const queries = lowerCaseQuery.split(" and ");
        tempTickets = tempTickets.filter((ticket) =>
          queries.every((query) => matches(ticket, query)),
        );
      } else if (lowerCaseQuery.includes(" or ")) {
        const queries = lowerCaseQuery.split(" or ");
        tempTickets = tempTickets.filter((ticket) =>
          queries.some((query) => matches(ticket, query)),
        );
      } else {
        tempTickets = tempTickets.filter((ticket) =>
          matches(ticket, lowerCaseQuery),
        );
      }
    }
    setFilteredTickets(tempTickets);
  }, [selectedButton, searchQuery, tickets]);

  useEffect(() => {
    const temCount = { total: filteredTickets.length };
    (status ?? []).forEach((s) => {
      const vals = [String(s.value), ...(s.aliases ?? []).map(String)];
      temCount[s.value] = filteredTickets.filter((t) =>
        vals.includes(String(t.status)),
      ).length;
    });
    setCount(temCount);
  }, [filteredTickets, status]);

  useEffect(() => {
    if (activeIndex === "total") {
      setFilteredByStates(filteredTickets);
      return;
    }
    const statusObj = (status ?? []).find((s) => s.value === activeIndex);
    const vals = statusObj
      ? [String(statusObj.value), ...(statusObj.aliases ?? []).map(String)]
      : [String(activeIndex)];
    setFilteredByStates(
      filteredTickets.filter((t) => vals.includes(String(t.status))),
    );
  }, [filteredTickets, activeIndex, tickets, status]);

  const sortValue = (t) => {
    const safe = (fn, v) => {
      try { return fn(v); } catch { return ""; }
    };
    switch (sortColumn) {
      case "requestType": return safe(getServiceRequest, t.requestType).toLowerCase();
      case "priority": return Number(t.priority);
      case "status": return Number(t.status);
      case "updatedAt": return new Date(t.updatedAt).getTime() || 0;
      case "createdAt":
      default: return new Date(t.createdAt).getTime() || 0;
    }
  };
  const sortedTickets = [...filteredByStates].sort((a, b) => {
    const va = sortValue(a);
    const vb = sortValue(b);
    const cmp =
      typeof va === "number" && typeof vb === "number"
        ? va - vb
        : String(va).localeCompare(String(vb));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const toggleSort = (key) => {
    if (sortColumn === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortColumn(key);
      setSortDir("asc");
    }
  };
  const sortableTh = (label, key) => (
    <th
      onClick={() => toggleSort(key)}
      className="p-4 text-xs font-bold tracking-wider uppercase text-gray-600 border-b border-gray-200 cursor-pointer select-none hover:bg-black/5 transition-colors whitespace-nowrap"
    >
      <span className="inline-flex items-center gap-1">
        {label}
        <span className="text-jiojbm50">
          {sortColumn === key ? (sortDir === "asc" ? "↑" : "↓") : ""}
        </span>
      </span>
    </th>
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredByStates.length / itemsPerPage),
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentTickets = sortedTickets.slice(
    indexOfFirstItem,
    indexOfLastItem,
  );

  function getServiceRequest(serviceId) {
    return requestOptions.find(
      (requestOption) => requestOption.value == serviceId,
    ).key;
  }

  function getPriority(priorityId) {
    return priorities.find((priority) => priority.value == priorityId).key;
  }

  function getStatus(statusId) {
    return status.find((stat) => stat.value == statusId).key;
  }

  // Amber alert pill for "Need More info", green pill for every other state —
  // mirrors the b-info / b-open badges in the RCS Support reference design.
  function statusBadgeClass(label) {
    return /more\s*info/i.test(label)
      ? "bg-[#fff7ed] text-[#c2620a]"
      : "bg-jiojbm20 text-jiojbm50";
  }

  // P1 reads as red, P2 as amber, anything else stays neutral.
  function severityClass(severity) {
    if(severity == 3) return "text-[#dc2626]"
    if(severity == 4) return "text-[#d97706]"
    return "text-gray-600";
  }

  useEffect(() => {
    setCurrentPage(1);
  }, [filteredByStates, sortColumn, sortDir]);

  return (
    <div className="flex flex-col">
      <h2 className="text-[22px] font-extrabold tracking-tight mb-4">Tickets</h2>

      <div className="flex items-center gap-4 flex-wrap mb-[18px]">
        <div className="inline-flex gap-1 bg-jiojbm20 border border-jiojbm25 rounded-full p-1">
          <button
            className={`flex items-center justify-center gap-2 px-[18px] py-2 text-sm font-semibold rounded-full transition whitespace-nowrap ${
              selectedButton === "allTickets"
                ? "bg-jiojbm40 text-white shadow-sm"
                : "text-jiojbm50 hover:bg-white/60"
            }`}
            onClick={() => handleButtonClick("allTickets")}
          >
            All Tickets
          </button>

          <button
            className={`flex items-center justify-center gap-2 px-[18px] py-2 text-sm font-semibold rounded-full transition whitespace-nowrap ${
              selectedButton === "myRaised"
                ? "bg-jiojbm40 text-white shadow-sm"
                : "text-jiojbm50 hover:bg-white/60"
            }`}
            onClick={() => handleButtonClick("myRaised")}
          >
            Raised by Me
          </button>
        </div>

        <label className="flex-1 min-w-[240px] flex items-center bg-white h-[46px] rounded-xl border border-gray-200 px-3.5 shadow-sm focus-within:border-jiojbm40 focus-within:ring-2 focus-within:ring-jiojbm25">
          <span className="w-[18px] h-[18px] fill-gray-400 shrink-0">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
              <path d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"></path>
            </svg>
          </span>
          <input
            className="w-full px-2.5 text-[15px] text-gray-700 leading-tight focus:outline-none"
            type="text"
            placeholder="Search by any field"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="relative group fill-gray-400 shrink-0">
            <svg
              className="w-[18px] h-[18px]"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2a10 10 0 100 20 10 10 0 000-20zm0 3.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm2 12h-4a1 1 0 010-2h1v-3h-1a1 1 0 010-2h2a1 1 0 011 1v4h1a1 1 0 010 2z"
                fill=""
              ></path>
            </svg>
            <div className="absolute hidden group-hover:flex right-1 z-10 top-6 p-2 shadow-[0_0_16px_rgba(0,0,0,0.16)] bg-white rounded-lg text-sm font-medium w-60">
              Separate search terms with "and" to find results containing both
              terms and "or" to find results containing at least one.
            </div>
          </div>
        </label>

        <button
          className="flex items-center gap-2 bg-jiojbm40 hover:bg-jiojbm50 px-[18px] h-[46px] text-sm font-semibold text-white rounded-xl transition whitespace-nowrap"
          onClick={() => navigate("/Form")}
        >
          <Plus size={16} strokeWidth={2.4} />
          Raise Ticket
        </button>
        <button
          className="flex items-center gap-2 bg-white border border-jiojbm40 text-jiojbm50 hover:bg-jiojbm20 px-[18px] h-[46px] text-sm font-semibold rounded-xl transition whitespace-nowrap"
          onClick={() => downLoadTickets()}
        >
          <Download size={16} />
          Download Tickets
        </button>
      </div>

      <div className="overflow-auto bg-white border border-gray-200 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,.04),0_8px_24px_rgba(15,23,42,.06)]">
        <table className="min-w-full text-left">
          <thead>
            <tr className="bg-jiojbm20">
              <th className="p-4 text-xs font-bold tracking-wider uppercase text-gray-600 border-b border-gray-200">
                Ticket ID
              </th>
              {sortableTh("Last Updated", "updatedAt")}
              {sortableTh("Service Request Type", "requestType")}
              {sortableTh("Severity", "priority")}
              {sortableTh("Status", "status")}
              <th className="p-4 text-xs font-bold tracking-wider uppercase text-gray-600 border-b border-gray-200">
                Assigned
              </th>
              {sortableTh("Created Date", "createdAt")}
            </tr>
          </thead>
          <tbody>
            {currentTickets.map((ticket) => (
              <tr
                key={ticket._id}
                onDoubleClick={() => navigate(`/Ticket/${ticket.ticketId}`)}
                className="cursor-pointer border-t hover:bg-jiojbm20"
              >
                <td className="p-4 text-sm">
                  <button
                    onClick={() => navigate(`/Ticket/${ticket.ticketId}`)}
                    className="font-bold text-jiojbm50 hover:underline"
                  >
                    {ticket.ticketId}
                  </button>
                </td>
                <td className="p-4 text-sm">{ticket.updatedAt.slice(0, 10)}</td>
                <td className="p-4 text-sm">
                  {getServiceRequest(ticket.requestType)}
                </td>
                {/* <td className="p-4 text-sm">
                  {ticket.issueAt}
                </td> */}
                <td className="p-4 text-sm">
                  <span
                    className={`font-bold tracking-wide ${severityClass(
                      ticket.priority
                    )}`}
                  >
                    {getPriority(ticket.priority)}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${statusBadgeClass(
                      getStatus(ticket.status),
                    )}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-current" />
                    {getStatus(ticket.status)}
                  </span>
                </td>
                <td className="p-4 text-sm">
                  {ticket.status == 5 || ticket.status == 3 || ticket.status == 4
                    ? ticket.createdBy
                    : "Sandesh NNI Support"}
                </td>
                <td className="p-4 text-sm">{ticket.createdAt.slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-center w-full px-2 mt-4">
        {/* Pagination Controls */}
        <div className="flex-1 flex gap-16 pl-32 justify-center items-center">
          <button
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className={`w-10 h-10 flex items-center justify-center rounded-full ${
              currentPage === 1
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-200"
            }`}
          >
            <div
              className={`relative flex items-center justify-center w-10 h-10 rounded-full cursor-pointer
        ${currentPage === 1 ? "fill-gray60" : "fill-jiojbm50 hover:bg-jiojbm20"}`}
            >
              <svg
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
              >
                <path d="M14 17a1.003 1.003 0 01-.71-.29l-4-4a1 1 0 010-1.42l4-4a1.005 1.005 0 011.42 1.42L11.41 12l3.3 3.29a.997.997 0 01.219 1.095.999.999 0 01-.93.615z"></path>
              </svg>
            </div>
          </button>

          <p className="text-gray-700">
            Page {currentPage} of {totalPages}
          </p>

          <button
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentPage === totalPages}
            className={`w-10 h-10 flex items-center justify-center rounded-full ${
              currentPage === totalPages
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-200"
            }`}
          >
            <div
              className={`relative flex items-center justify-center rotate-180 w-10 h-10 rounded-full cursor-pointer
        ${
          currentPage === totalPages
            ? "fill-gray60"
            : "fill-jiojbm50 hover:bg-jiojbm20"
        }`}
            >
              <svg
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className="w-6 h-6"
              >
                <path d="M14 17a1.003 1.003 0 01-.71-.29l-4-4a1 1 0 010-1.42l4-4a1.005 1.005 0 011.42 1.42L11.41 12l3.3 3.29a.997.997 0 01.219 1.095.999.999 0 01-.93.615z"></path>
              </svg>
            </div>
          </button>
        </div>

        {/* Select Dropdown at End */}
        <div className="ml-auto flex gap-x-2 items-center">
          <p className="font-bold text-jiojbmpurple tracking-tight leading-tight text-sm whitespace-nowrap">
            Tickets Per Page
          </p>
          <select
            className="border border-gray-300 rounded-full px-3 py-1"
            value={itemsPerPage}
            onChange={(e) => setItemsPerPage(e.target.value)}
          >
            {Array.from({ length: 10 }, (_, i) => (
              <option key={i} value={5 + i * 5}>
                {5 + i * 5}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};
